import React, { useRef, useState } from "react";

// ASSETS
import CoverPlaceholder from "assets/img/CoverPlaceholder.png";
import ArroowGrey from "assets/svg/ArrowDown.svg";
import ArrowRed from "assets/svg/ArrowDownRed.svg";
import XSign from "assets/svg/XSignModal.svg";

// STYLES

import {
  OfficialsCarouselContainer,
  OfficialsSlider,
  OfficialsSliderItem,
  CarouselScrollbar,
  CarouselDot,
  CarouselDotButton,
  OfficialsCardContainer,
  OfficialsCardImage,
  NavButton,
  HeaderComponent,
  NavButtonsWrapper,
  LeftImgButton,
  RightImgButton,
  Modal,
  ModalContent,
  ModalCloseButton,
  ImgModal,
  XImg,
  ModalContentWrapper,
} from "./OfficialsCarouselStyled";

// LIBRARIES

import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import Slider from "react-slick";
import { useMediaQuery } from "react-responsive";
import { useTranslation } from "react-i18next";

// MISC

import { IProps, navState } from "./OfficialsCarouselModel";
import { TextType } from "components/Atoms/CustomText/CustomTextModel";

// REDUX

import { useSelector } from "react-redux";
import { accessibilityBiggerCursorSelector } from "slices/UISlice/uiSliceSelectors";

// COMPONENTS
import CustomText from "components/Atoms/CustomText/CustomText";

const OfficialsCarousel = (props: IProps) => {
  // PROPERTIES
  const { data } = props;

  //LIBRARY

  const isMobile = useMediaQuery({ maxWidth: 1024 });
  const { t } = useTranslation();
  const biggerCursor = useSelector(accessibilityBiggerCursorSelector);

  //STATE
  const [navState, setNavState] = useState<navState>({
    isPrevAvailable: false,
    isNextAvailable: true,
  });
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  // Slider reference
  const sliderRef = useRef<Slider | null>(null);

  // Slider settings
  const settings = {
    infinite: false,
    speed: 500,
    slidesToShow: 5,
    slidesToScroll: 1,
    centerMode: false,
    beforeChange: (current, next) => {
      setNavState({
        isPrevAvailable: next > 0,
        isNextAvailable: next < data?.length - 5,
      });
    },
    responsive: [
      {
        breakpoint: 3700,
        settings: {
          centerPadding: "850px",
        },
      },
      {
        breakpoint: 3500,
        settings: {
          centerPadding: "800px",
        },
      },
      {
        breakpoint: 3300,
        settings: {
          centerPadding: "750px",
        },
      },
      {
        breakpoint: 3100,
        settings: {
          centerPadding: "700px",
        },
      },
      {
        breakpoint: 2900,
        settings: {
          centerPadding: "650px",
        },
      },
      {
        breakpoint: 2700,
        settings: {
          centerPadding: "600px",
        },
      },
      {
        breakpoint: 2500,
        settings: {
          centerPadding: "550px",
        },
      },
      {
        breakpoint: 2300,
        settings: {
          centerPadding: "500px",
        },
      },
      {
        breakpoint: 2100,
        settings: {
          centerPadding: "450px",
        },
      },
      {
        breakpoint: 1720,
        settings: {
          centerPadding: "300px",
        },
      },
      {
        breakpoint: 1520,
        settings: {
          centerPadding: "260px",
        },
      },
      {
        breakpoint: 1400,
        settings: {
          centerPadding: "230px",
        },
      },
      {
        breakpoint: 1300,
        settings: {
          centerPadding: "200px",
        },
      },
      {
        breakpoint: 1200,
        settings: {
          centerPadding: "140px",
        },
      },
      {
        breakpoint: 1100,
        settings: {
          centerPadding: "100px",
        },
      },
      {
        breakpoint: 1024,
        settings: {
          centerPadding: "120px",
          dots: true,
          appendDots: (dots) => (
            <CarouselScrollbar>
              <CarouselDot> {dots} </CarouselDot>
            </CarouselScrollbar>
          ),
          customPaging: () => <CarouselDotButton />,
        },
      },
      {
        breakpoint: 900,
        settings: {
          slidesToShow: 3,
          centerPadding: "120px",
          dots: true,
          appendDots: (dots) => (
            <CarouselScrollbar>
              <CarouselDot> {dots} </CarouselDot>
            </CarouselScrollbar>
          ),
          customPaging: () => <CarouselDotButton />,
        },
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 2,
          dots: true,
          centerPadding: "30px",
          appendDots: (dots) => (
            <CarouselScrollbar>
              <CarouselDot> {dots} </CarouselDot>
            </CarouselScrollbar>
          ),
          customPaging: () => <CarouselDotButton />,
        },
      },
      {
        breakpoint: 650,
        settings: {
          slidesToShow: 1,
          dots: true,
          centerMode: true,
          centerPadding: "100px",
          appendDots: (dots) => (
            <CarouselScrollbar>
              <CarouselDot> {dots} </CarouselDot>
            </CarouselScrollbar>
          ),
          customPaging: () => <CarouselDotButton />,
        },
      },
      {
        breakpoint: 590,
        settings: {
          slidesToShow: 1,
          dots: true,
          centerMode: true,
          centerPadding: "80px",
          appendDots: (dots) => (
            <CarouselScrollbar>
              <CarouselDot> {dots} </CarouselDot>
            </CarouselScrollbar>
          ),
          customPaging: () => <CarouselDotButton />,
        },
      },
      {
        breakpoint: 550,
        settings: {
          slidesToShow: 1,
          centerPadding: "60px",
          centerMode: true,
          dots: true,
          appendDots: (dots) => (
            <CarouselScrollbar>
              <CarouselDot> {dots} </CarouselDot>
            </CarouselScrollbar>
          ),
          customPaging: () => <CarouselDotButton />,
        },
      },
      {
        breakpoint: 510,
        settings: {
          slidesToShow: 1,
          centerPadding: "60px",
          centerMode: true,
          dots: true,
          appendDots: (dots) => (
            <CarouselScrollbar>
              <CarouselDot> {dots} </CarouselDot>
            </CarouselScrollbar>
          ),
          customPaging: () => <CarouselDotButton />,
        },
      },
      {
        breakpoint: 500,
        settings: {
          slidesToShow: 1,
          centerPadding: "40px",
          centerMode: true,
          dots: true,
          appendDots: (dots) => (
            <CarouselScrollbar>
              <CarouselDot> {dots} </CarouselDot>
            </CarouselScrollbar>
          ),
          customPaging: () => <CarouselDotButton />,
        },
      },
      {
        breakpoint: 430,
        settings: {
          slidesToShow: 1,
          centerPadding: "30px",
          centerMode: true,
          dots: true,
          appendDots: (dots) => (
            <CarouselScrollbar>
              <CarouselDot> {dots} </CarouselDot>
            </CarouselScrollbar>
          ),
          customPaging: () => <CarouselDotButton />,
        },
      },
    ],
  };

  // API REQUESTS

  // LIFE CYCLE

  // EVENT HANDLERS
  const openModal = (image: string) => {
    setSelectedImage(image);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setSelectedImage(null);
    setIsModalOpen(false);
  };

  return (
    <OfficialsCarouselContainer>
      <HeaderComponent>
        <CustomText
          text={t(
            "view.newsAndMedia.officialAnnouncementsDetails.photoGallery"
          )}
          type={TextType.H2bold}
        />
        {!isMobile && (
          <NavButtonsWrapper>
            <NavButton
              onClick={() => sliderRef.current?.slickPrev()}
              biggerCursor={biggerCursor}
            >
              <LeftImgButton
                src={navState.isPrevAvailable ? ArrowRed : ArroowGrey}
                alt="arrow"
              />
            </NavButton>
            <NavButton
              onClick={() => sliderRef.current?.slickNext()}
              biggerCursor={biggerCursor}
            >
              <RightImgButton
                src={navState.isNextAvailable ? ArrowRed : ArroowGrey}
                alt="arrow"
              />
            </NavButton>
          </NavButtonsWrapper>
        )}
      </HeaderComponent>

      <OfficialsSlider ref={sliderRef} {...settings}>
        {data?.map((item) => (
          <OfficialsSliderItem key={item?.id}>
            <OfficialsCardContainer onClick={() => openModal(item?.image)}>
              <OfficialsCardImage
                src={item?.image?.trim() ? item.image : CoverPlaceholder}
              />
            </OfficialsCardContainer>
          </OfficialsSliderItem>
        ))}
      </OfficialsSlider>
      {isModalOpen && (
        <Modal onClick={closeModal}>
          <ModalContentWrapper>
            <ModalCloseButton onClick={closeModal} biggerCursor={biggerCursor}>
              <XImg src={XSign} alt="close" />
            </ModalCloseButton>
            <ModalContent>
              <ImgModal
                src={selectedImage?.trim() ? selectedImage : CoverPlaceholder}
                alt="Selected"
              />
            </ModalContent>
          </ModalContentWrapper>
        </Modal>
      )}
    </OfficialsCarouselContainer>
  );
};

export default OfficialsCarousel;
